import React, { FC, useCallback, useMemo, useState } from 'react';
import { contactObjectExists, nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import DateTimeHover from '../date_time_component/DateTime';
import { UserName } from '../name_component/user/UserNameComponent';
import { Button, message } from 'antd';
import { ScheduleOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	walkthroughsRestCrudThunksForBuyer,
	walkthroughsRestCrudThunksForSupplier,
} from '../../thunks/walkthroughs_thunks';
import { withRouter } from 'react-router';
import { SimpleWalkthroughRowDisplay } from '../walkthrough_row_display/WalkthroughRowDisplay';
import moment from 'moment';
import { withUserType } from '../withUserType';

const WOTaskAdditionalDetails: FC<any> = ({
	task,
	timeZone,
	userType,
	onSave,
	workOrder,

	currentUser,
	createWalkthrough,
}): React.ReactElement => {
	const [walkthroughScheduling, setWalkthroughScheduling] = useState(false);

	const canShowAnsweredBy = useMemo(
		() => task.answer === 'true' && contactObjectExists(task.answeredByContact),
		[task.answer, task.answeredByContact]
	);

	const onWalkthroughScheduleSuccess = useCallback(
		(walkthrough) => {
			onSave({
				...task,
				walkthroughId: walkthrough.id,
			});
		},
		[onSave, task]
	);

	const onScheduleWalkthrough = useCallback(() => {
		const siteSurvey = nullSafeGet('siteSurvey', task);

		if (!siteSurvey || !workOrder) return;

		setWalkthroughScheduling(true);
		const dueAt = nullSafeGet('woPriority.expectedResolutionInSeconds', workOrder)
			? moment().add(nullSafeGet('woPriority.expectedResolutionInSeconds', workOrder), 'seconds')
			: undefined;
		const updateValues = {
			dueAt,
			title: siteSurvey.title + ' walkthrough',
			status: 'incomplete',
			locationId: workOrder.locationId,
			siteSurveyId: siteSurvey.id,
			scheduledBy: currentUser.email,
		};

		createWalkthrough(updateValues)
			.then((walkthrough) => {
				onWalkthroughScheduleSuccess(walkthrough);
			})
			.catch(message.error)
			.finally(() => setWalkthroughScheduling(false));
	}, [createWalkthrough, currentUser.email, onWalkthroughScheduleSuccess, task, workOrder]);

	const siteSurveyDetails = useMemo(
		() =>
			nullSafeGet('siteSurvey', task) ? (
				<div className="mt-2 flex flex-row items-center justify-between text-gray-400">
					<div className="flex flex-row items-center">
						<div>{`Site Survey : ${nullSafeGetOrElse('siteSurvey.title', task, '--')}`}</div>
						<div className="ml-4">
							<span className="rowGrayChip">
								<i className="icons8-font icons8-location-marker" />
								{nullSafeGetOrElse('siteSurvey.location.name', task, '--')}
							</span>
						</div>
					</div>
					{!task.walkthroughId && onSave ? (
						<Button
							type="link"
							icon={<ScheduleOutlined />}
							onClick={onScheduleWalkthrough}
							loading={walkthroughScheduling}
						>
							Walkthrough
						</Button>
					) : null}
				</div>
			) : null,
		[onSave, onScheduleWalkthrough, task, walkthroughScheduling]
	);

	const siteSurveyTemplateDetails = useMemo(() => {
		const sstIds = nullSafeGetOrElse('siteSurveyTemplateIds', task, []);
		const ssts = sstIds
			.map((id) => nullSafeGetOrElse('siteSurveyTemplates', task, []).find((_) => _.id === id))
			.filter((_) => !!_);
		return nullSafeGet('0', ssts) ? (
			<div className="flex flex-col gap-2">
				{ssts.map((sst, idx) => (
					<div key={`${task.id}-sst-${sst.id}`} className="flex flex-row items-center gap-2">
						<i className="icons8-font icons8-clipboard-checklist" />
						{sst.title}
					</div>
				))}
			</div>
		) : null;
	}, [task]);

	const walkthroughLink = useMemo(
		() =>
			`/${userType}/siteSurveys/walkthrough/detail/${nullSafeGet('walkthroughId', task)}/details`,
		[task, userType]
	);

	const walkthroughDetails = useMemo(
		() =>
			nullSafeGet('walkthrough', task) ? (
				<div className="mt-2">
					<SimpleWalkthroughRowDisplay
						walkthrough={nullSafeGet('walkthrough', task)}
						walkthroughLink={walkthroughLink}
					/>
				</div>
			) : null,
		[task, walkthroughLink]
	);

	return (
		<div className="ml-6 flex flex-col">
			{siteSurveyDetails}
			{walkthroughDetails}
			{siteSurveyTemplateDetails}
			{canShowAnsweredBy ? (
				<div className="mt-2 flex flex-row items-center text-gray-400">
					{task.answeredByContact ? (
						<>
							<div className="mr-1">Completed by</div>
							<UserName contact={task.answeredByContact} mode={'inline'} placement={'top'} />
						</>
					) : null}
					{task.answeredAt && (
						<div className="flex flex-row items-center">
							<div className="mx-1">at</div>
							<DateTimeHover timestamp={task.answeredAt} showDate showTime timeZone={timeZone} />
						</div>
					)}
				</div>
			) : null}
		</div>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	createWalkthrough: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? walkthroughsRestCrudThunksForSupplier.create(entity)
				: walkthroughsRestCrudThunksForBuyer.create(entity)
		),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(WOTaskAdditionalDetails))
);
